import { useState } from "react";
import { Button, DialogActions, DialogContent, TextField, CircularProgress, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import axios from "axios";
import { ReactComponent as AvatarImage } from "../../../assets/directtv_avatar.svg";
import { DealerAvatar } from "../../atoms";
import * as S from "./styles";
import { colors } from "@/theme";
import { useIsAdminMode } from "@/contexts";

export type ChangePasswordDialogProps = {
  open?: boolean;
  onClose?: () => void;
};

const textFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: colors.fieldBg,
    "font-family": "PF DIN Text Pro",
    "font-size": "14px",
    "font-style": "normal",
    "font-weight": "400",
    "line-height": "16px",
  },
};

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open = false,
  onClose,
}) => {
  const { currentAdminData } = useIsAdminMode();
  const [email, setEmail] = useState(currentAdminData?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    email: false,
    oldPassword: false,
    newPassword: false,
    confirmNewPassword: false,
  });

  const handleClose = () => {
    // Reset all fields
    setEmail(currentAdminData?.email || "");
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setErrorMessage("");
    setSuccessMessage("");
    setFieldErrors({
      email: false,
      oldPassword: false,
      newPassword: false,
      confirmNewPassword: false,
    });
    onClose?.();
  };

  const handleChangePassword = async () => {
    // Reset errors
    setErrorMessage("");
    setSuccessMessage("");
    setFieldErrors({
      email: false,
      oldPassword: false,
      newPassword: false,
      confirmNewPassword: false,
    });

    // Validate fields
    const errors = {
      email: !email,
      oldPassword: !oldPassword,
      newPassword: !newPassword,
      confirmNewPassword: !confirmNewPassword,
    };

    // Check if passwords match
    if (newPassword && confirmNewPassword && newPassword !== confirmNewPassword) {
      setErrorMessage("New passwords do not match");
      setFieldErrors({
        ...errors,
        newPassword: true,
        confirmNewPassword: true,
      });
      return;
    }

    // Check if any required field is empty
    if (Object.values(errors).some(error => error)) {
      setErrorMessage("All fields are required");
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_REST_BASE_URL}/User/change-password`,
        {
          email,
          oldPassword,
          newPassword,
        }
      );

      if (response.data.success) {
        setSuccessMessage("Password changed successfully!");
        // Clear password fields after success
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setErrorMessage(response.data.message || "Failed to change password");
        setFieldErrors({
          email: true,
          oldPassword: true,
          newPassword: false,
          confirmNewPassword: false,
        });
      }
    } catch (error: any) {
      console.error("Change password error:", error);
      const errorMsg = error.response?.data?.message || "Failed to change password. Please try again.";
      setErrorMessage(errorMsg);
      setFieldErrors({
        email: true,
        oldPassword: true,
        newPassword: false,
        confirmNewPassword: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogContent>
        <S.ChangePasswordDialogContentContainer>
          <S.ChangePasswordDialogContentAvatarContainer>
            <DealerAvatar children={<AvatarImage />} width={96} height={96} />
          </S.ChangePasswordDialogContentAvatarContainer>
          <S.ChangePasswordDialogContentTitle>
            CHANGE PASSWORD
          </S.ChangePasswordDialogContentTitle>
          <TextField
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorMessage("");
              setFieldErrors(prev => ({ ...prev, email: false }));
            }}
            value={email}
            autoFocus
            id="email"
            label="Email"
            type="email"
            fullWidth
            error={fieldErrors.email}
            helperText={fieldErrors.email ? "Email is required" : undefined}
            placeholder="Enter your email"
            sx={textFieldSx}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => {
              setOldPassword(e.target.value);
              setErrorMessage("");
              setFieldErrors(prev => ({ ...prev, oldPassword: false }));
            }}
            value={oldPassword}
            id="oldPassword"
            label="Old Password"
            type="password"
            fullWidth
            error={fieldErrors.oldPassword}
            helperText={fieldErrors.oldPassword ? "Old password is required" : undefined}
            placeholder="Enter your old password"
            sx={textFieldSx}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrorMessage("");
              setFieldErrors(prev => ({ ...prev, newPassword: false }));
            }}
            value={newPassword}
            id="newPassword"
            label="New Password"
            type="password"
            fullWidth
            error={fieldErrors.newPassword}
            helperText={fieldErrors.newPassword ? "New password is required" : undefined}
            placeholder="Enter your new password"
            sx={textFieldSx}
            disabled={isLoading}
          />
          <TextField
            onChange={(e) => {
              setConfirmNewPassword(e.target.value);
              setErrorMessage("");
              setFieldErrors(prev => ({ ...prev, confirmNewPassword: false }));
            }}
            value={confirmNewPassword}
            id="confirmNewPassword"
            label="Confirm New Password"
            type="password"
            fullWidth
            error={fieldErrors.confirmNewPassword}
            helperText={fieldErrors.confirmNewPassword ? "Please confirm your new password" : undefined}
            placeholder="Confirm your new password"
            sx={textFieldSx}
            disabled={isLoading}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleChangePassword();
              }
            }}
          />
          {errorMessage && (
            <Typography
              sx={{ 
                color: "#d32f2f", 
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              sx={{ 
                color: "#4caf50", 
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
                fontFamily: "PF DIN Text Pro",
              }}
            >
              {successMessage}
            </Typography>
          )}
        </S.ChangePasswordDialogContentContainer>
      </DialogContent>
      <DialogActions
        sx={{
          "&.MuiDialogActions-root": {
            "justify-content": "center",
            padding: "24px",
          },
        }}
      >
        <Button
          onClick={handleChangePassword}
          disabled={isLoading}
          sx={{
            width: "100%",
            color: colors.white,
            background: colors.ozone,
            "border-radius": "12px",
            padding: "12px",
            "font-family": "PF DIN Text Pro",
            "font-size": "12px",
            "font-style": "normal",
            "font-weight": "700",
            "line-height": "16px",
            "text-transform": "uppercase",
            "&:hover": {
              color: colors.white,
              background: colors.ozone,
            },
            "&.Mui-disabled": {
              color: colors.white,
              background: colors.neutralGrey500,
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: colors.white }} />
          ) : (
            "CHANGE PASSWORD"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
